import { Outlet, useParams } from 'react-router-dom'

import type { PublicMember, PublicPatient } from '@snootsvet/shared/portal'

import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { NotFound } from '@/customer/components/not-found'
import { useGetMe } from '@/customer/hooks/use-get-me'

/** Outlet context published by `<PetScope />`. */
export type PetScopeContext = { pet: PublicPatient; member: PublicMember }

/**
 * Layout route for `/account/pets/:patientId/*` and any other route
 * whose URL contract includes a `:patientId` owned by the signed-in
 * member. Resolves the pet once, renders a 404 when the id doesn't
 * match anything the member owns, and passes the pet down to children
 * via the outlet context.
 *
 * Deliberately 404s rather than redirects on unknown ids: a bad
 * `:patientId` is either a stale bookmark or a bug in our own link
 * construction, and both are easier to diagnose in prod when the
 * customer lands on a visible error page instead of silently ending
 * up somewhere else.
 */
export function PetScope() {
  const { patientId } = useParams<{ patientId: string }>()
  const me = useGetMe()

  if (me.isPending) return <LoadingSpinner />

  const pet = me.data?.patients.find((p) => p.id === patientId)
  if (!pet || !me.data) return <NotFound />

  return <Outlet context={{ pet, member: me.data.member } satisfies PetScopeContext} />
}
